import { useCallback } from "react";

import { useLazyQuery, useMutation } from "@apollo/client";
import { usePostHog } from "@posthog/react";
import { toast } from "sonner";

/** @typedef {import('graphql').DocumentNode} DocumentNode */

/**
 * Custom hook that returns a standardized error handler with toast and PostHog integration.
 *
 * - Logs the error to console.
 * - Sends the error to PostHog.
 * - Displays a toast notification.
 * - Allows custom error formatting via `onError` callback.
 *
 * @param {function} [onError] - Optional error handler callback. Can return:
 *   - `undefined` to use default message.
 *   - A string to override error message.
 *   - An object `{ title, message }` to override toast content.
 * @returns {(error: Error) => void} - A callback function to handle errors.
 */
const useHandleError = (onError) => {
  const postHog = usePostHog();

  return useCallback(
    (e) => {
      console.error(e);
      postHog.captureException(e);

      let errorTitle = "An error occurred";
      let errorMessage = e.message || "Something went wrong. Please try again.";

      if (onError) {
        const compositeError = onError(e);
        if (compositeError !== undefined) {
          if (typeof compositeError === "object") {
            errorTitle = compositeError.title;
            errorMessage = compositeError.message;
          } else {
            errorMessage = compositeError;
          }
        }
      }

      if (errorMessage) {
        toast.error(errorTitle, {
          description: errorMessage,
        });
      }
    },
    [onError, postHog]
  );
};

/**
 * Custom hook to execute GraphQL queries with manual triggering, error handling, and cache support.
 *
 * - Uses Apollo's `useLazyQuery` under the hood.
 * - Only triggers network request when `queryApi()` is called.
 * - Automatically handles errors via `useHandleError`.
 *
 * @param {object} params
 * @param {DocumentNode} params.query - GraphQL query document.
 * @param {function} [params.onSuccess] - Optional callback on successful query completion.
 * @param {function} [params.onError] - Optional custom error handler (see `useHandleError`).
 * @param {object} [params.apolloOptions] - Optional additional options to pass to Apollo's `useLazyQuery`.
 * @returns {object} Object containing:
 *   - `queryApi(variables)` - Function to trigger the query.
 *   - `data` - Query result data.
 *   - `loading` - Loading state.
 *   - `error` - Apollo error object.
 *   - `refetch` - Apollo's refetch function.
 */
export const useApiQuery = ({
  query,
  onSuccess,
  onError,
  apolloOptions = {},
}) => {
  const handleError = useHandleError(onError);

  const [fetchQuery, { data, loading, error, refetch }] = useLazyQuery(query, {
    fetchPolicy: "network-only",
    onCompleted: onSuccess,
    onError: handleError,
    ...apolloOptions,
  });

  const queryApi = useCallback(
    (variables = undefined) => fetchQuery({ variables }),
    [fetchQuery]
  );

  return { queryApi, data, loading, error, refetch };
};

/**
 * Custom hook to execute GraphQL mutations with error handling.
 *
 * - Uses Apollo's `useMutation` under the hood.
 * - Automatically handles errors via `useHandleError`.
 *
 * @param {object} params
 * @param {DocumentNode} params.mutation - GraphQL mutation document.
 * @param {function} [params.onSuccess] - Optional callback on successful mutation completion.
 * @param {function} [params.onError] - Optional custom error handler (see `useHandleError`).
 * @param {object} [params.apolloOptions] - Optional additional options to pass to Apollo's `useMutation`.
 * @returns {object} Object containing:
 *   - `mutationApi(variables)` - Function to trigger the mutation.
 *   - `data` - Mutation result data.
 *   - `loading` - Loading state.
 *   - `error` - Apollo error object.
 */
export const useApiMutation = ({
  mutation,
  onSuccess,
  onError,
  apolloOptions = {},
}) => {
  const handleError = useHandleError(onError);

  const [mutate, { data, loading, error }] = useMutation(mutation, {
    onCompleted: onSuccess,
    onError: handleError,
    ...apolloOptions,
  });

  const mutationApi = useCallback(
    (variables) => mutate({ variables }),
    [mutate]
  );

  return { mutationApi, data, loading, error };
};

/**
 * Hook to safely wrap any async function with standardized error handling.
 *
 * @param {function} [onError] - Optional custom error handler (same shape as in useHandleError).
 * @returns {(fn: function) => function} - Function wrapper to safely catch & handle errors.
 */
export const useSafeWrapper = (onError) => {
  const handleError = useHandleError(onError);

  return useCallback(
    (fn) => {
      return async (...args) => {
        try {
          return await fn(...args);
        } catch (e) {
          handleError(e);
        }
      };
    },
    [handleError]
  );
};
